import { findOneTrialApplication, mapClientError, queryKeys } from "@dutyon/apis";
import {
  ActionIcon,
  Alert,
  Button,
  Card,
  Grid,
  Group,
  LoadingOverlay,
  Stack,
  Text,
  Title,
  TitleContainer,
} from "@dutyon/ui-kit";
import { useQuery } from "@tanstack/react-query";
import { isEmpty } from "lodash";
import { ChevronLeft } from "lucide-react";
import { useNavigate, useParams } from "react-router-dom";
import { parseTrialApplicationSurvey } from "../../utils/trialApplication";

export function TrialApplicationDetailPage() {
  const { id: routeId } = useParams<{ id: string }>();
  const id = routeId ?? "";
  const navigate = useNavigate();

  const {
    data: application,
    isLoading,
    isError,
    error,
    refetch,
  } = useQuery({
    queryKey: queryKeys.trialApplications.detail(id),
    queryFn: () => findOneTrialApplication(id),
    enabled: Boolean(id),
  });

  if (isLoading) {
    return (
      <TitleContainer
        size="lg"
        py="xl"
        title={
          <Group gap="xs">
            <ActionIcon variant="subtle" onClick={() => navigate("/trial-applications")}>
              <ChevronLeft size={20} />
            </ActionIcon>
            <Title order={2}>체험하기 신청 상세</Title>
          </Group>
        }
      >
        <LoadingOverlay visible />
      </TitleContainer>
    );
  }

  if (isError || !application) {
    return (
      <TitleContainer size="lg" py="xl" title="체험하기 신청 상세">
        <Alert color="red" title="신청 정보를 불러오지 못했습니다.">
          <Stack gap="sm">
            <Text size="sm">
              {mapClientError(error, { fallback: "잠시 후 다시 시도해 주세요." }).message}
            </Text>
            <Group>
              <Button size="xs" onClick={() => void refetch()}>
                다시 시도
              </Button>
              <Button size="xs" variant="default" onClick={() => navigate("/trial-applications")}>
                목록으로
              </Button>
            </Group>
          </Stack>
        </Alert>
      </TitleContainer>
    );
  }

  const surveyData = parseTrialApplicationSurvey(application.surveyData);

  return (
    <TitleContainer
      size="lg"
      py="xl"
      title={
        <Group gap="xs">
          <ActionIcon variant="subtle" onClick={() => navigate("/trial-applications")}>
            <ChevronLeft size={20} />
          </ActionIcon>
          <Title order={2}>체험하기 신청 상세</Title>
        </Group>
      }
    >
      <Stack gap="xl">
        <Grid gutter="xl">
          <Grid.Col span={{ base: 12, md: 6 }}>
            <Stack gap="md">
              <Card withBorder p="lg" radius="md">
                <Title order={5} mb="md">
                  신청자 정보
                </Title>
                <Stack gap="md">
                  <div>
                    <Text size="xs" c="dimmed" mb={4}>
                      병원명
                    </Text>
                    <Text fw={500}>{application.hospitalName}</Text>
                  </div>
                  <div>
                    <Text size="xs" c="dimmed" mb={4}>
                      신청인 성함
                    </Text>
                    <Text>{application.name}</Text>
                  </div>
                  <div>
                    <Text size="xs" c="dimmed" mb={4}>
                      직책 / 포지션
                    </Text>
                    <Text>{application.position}</Text>
                  </div>
                  <div>
                    <Text size="xs" c="dimmed" mb={4}>
                      이메일 주소
                    </Text>
                    <Text>{application.email}</Text>
                  </div>
                  <div>
                    <Text size="xs" c="dimmed" mb={4}>
                      전화번호
                    </Text>
                    <Text>{String(surveyData.phone || "미기입")}</Text>
                  </div>
                </Stack>
              </Card>
            </Stack>
          </Grid.Col>

          <Grid.Col span={{ base: 12, md: 6 }}>
            <Stack gap="md">
              <Card withBorder p="lg" radius="md">
                <Title order={5} mb="md">
                  추가 설문 정보
                </Title>
                <Stack gap="md">
                  <div>
                    <Text size="xs" c="dimmed" mb={4}>
                      간호사 수
                    </Text>
                    <Text>{String(surveyData.nurseCount || "미기입")}</Text>
                  </div>
                  <div>
                    <Text size="xs" c="dimmed" mb={4}>
                      알게 된 경로
                    </Text>
                    <Text>
                      {String(surveyData.referralSource || surveyData.funnel || "미기입")}
                    </Text>
                  </div>
                  <div>
                    <Text size="xs" c="dimmed" mb={4}>
                      추가 설문 답변
                    </Text>
                    {isEmpty(
                      Object.keys(surveyData).filter(
                        (k) => !["phone", "nurseCount", "funnel", "referralSource"].includes(k),
                      ),
                    ) ? (
                      <Text size="sm" c="dimmed">
                        답변 없음
                      </Text>
                    ) : (
                      Object.entries(surveyData)
                        .filter(
                          ([key]) =>
                            !["phone", "nurseCount", "funnel", "referralSource"].includes(key),
                        )
                        .map(([key, val]) => (
                          <div key={key} style={{ marginTop: 8 }}>
                            <Text size="xs" fw={600} c="indigo">
                              Q. {key}
                            </Text>
                            <Text size="sm">A. {String(val)}</Text>
                          </div>
                        ))
                    )}
                  </div>
                </Stack>
              </Card>
            </Stack>
          </Grid.Col>
        </Grid>

        <Group justify="flex-end" gap="md">
          <Button variant="default" size="md" onClick={() => navigate("/trial-applications")}>
            목록으로
          </Button>
          <Button
            size="md"
            onClick={() =>
              navigate("/companies/create", {
                state: {
                  name: application.hospitalName,
                  adminName: application.name,
                  adminEmail: application.email,
                },
              })
            }
          >
            병원 계정 생성하기
          </Button>
        </Group>
      </Stack>
    </TitleContainer>
  );
}
